import { Course, FieldWithBbox } from '../models/transcriptExtractionData.model';

/**
 * Reads a field's value as a trimmed string, or '' when it has none.
 * @param field The extracted field
 */
export function extractValue(field: FieldWithBbox | undefined): string {
  return field?.value !== undefined && field?.value !== null ? field.value.toString().trim() : '';
}

/**
 * Reads a field's value as a number, or 0 when it has none.
 * @param field The extracted field
 */
export function extractNumericValue(field: FieldWithBbox | undefined): number {
  const value = parseFloat(extractValue(field));
  return isNaN(value) ? 0 : value;
}

/**
 * Turns an extraction key into the name the export sends: FirstName -> firstName,
 * CEEB -> ceeb.
 * @param key The extraction key
 */
function toExportKey(key: string): string {
  return key === key.toUpperCase() ? key.toLowerCase() : key[0].toLowerCase() + key.slice(1);
}

/**
 * Unwraps a section of the extraction data into the flat shape the export sends.
 *
 * For sections whose fields differ from the contract only in their key's first letter:
 * the student and, for high school, the institution. Courses are not one of them.
 * @param section The student or institution as the view holds it
 */
export function mapSection(
  section: Record<string, FieldWithBbox> | undefined
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(section ?? {}).map(([key, field]) => [toExportKey(key), extractValue(field)])
  );
}

/**
 * Maps courses onto the flat shape the export sends.
 * @param courses The extracted courses
 */
export function mapCourses(courses: Course[]) {
  return courses.map(course => ({
    term: extractValue(course.Term),
    startDate: extractValue(course.StartDate),
    endDate: extractValue(course.EndDate),
    subject: extractValue(course.Subject),
    courseNumber: extractValue(course.CourseNumber),
    courseTitle: extractValue(course.CourseTitle),
    creditsEarned: extractNumericValue(course.CreditsEarned),
    grade: extractValue(course.Grade),
    points: extractNumericValue(course.GradePoints),
    institution: extractValue(course.Institution),
    institutionId: extractValue(course.InstitutionId)
  }));
}
